import React, { useState } from 'react'
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Button, Checkbox, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import axios from 'axios'

const CAT_COLORS = {
  'Irrigation': '#3b82f6', 'Planting': '#10b981', 'Hardscape': '#6366f1',
  'Earthwork': '#d97706', 'Demo': '#ef4444', 'Lighting': '#f59e0b',
  'Chemical': '#8b5cf6', 'Turf': '#84cc16', 'Signage': '#06b6d4',
  'General': '#6b7280'
}

const ServiceStage = ({ assemblies, url, projectId, services, onRefresh }) => {
  const [selected, setSelected] = useState(new Set())
  const [suggestions, setSuggestions] = useState({})
  const [loadingSuggestions, setLoadingSuggestions] = useState({})
  const [bulkService, setBulkService] = useState(null)

  const API = `${url}/api/bid-estimation`

  // Group assemblies by category
  const groups = {}
  for (const asm of assemblies) {
    const cat = asm.category || 'Uncategorized'
    if (!groups[cat]) groups[cat] = []
    groups[cat].push(asm)
  }

  const toggleSelect = (id) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const handleSuggest = async (assemblyId) => {
    try {
      setLoadingSuggestions(prev => ({ ...prev, [assemblyId]: true }))
      const res = await axios.get(`${API}/assembly/${assemblyId}/service-suggestions`)
      setSuggestions(prev => ({ ...prev, [assemblyId]: res.data }))
    } catch (err) {
      console.error('Suggest failed:', err)
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, [assemblyId]: false }))
    }
  }

  const handleAssignSingle = async (assemblyId, serviceId, serviceName) => {
    try {
      await axios.put(`${API}/assembly/${assemblyId}/service`, { serviceId, serviceName })
      setSuggestions(prev => ({ ...prev, [assemblyId]: undefined }))
      onRefresh()
    } catch (err) {
      console.error('Assign failed:', err)
    }
  }

  const handleBulkAssign = async () => {
    if (!bulkService || selected.size === 0) return
    try {
      await axios.post(`${API}/assembly/bulk-assign-service`, {
        assemblyIds: [...selected],
        serviceId: bulkService.serviceId,
        serviceName: bulkService.name
      })
      setSelected(new Set())
      setBulkService(null)
      onRefresh()
    } catch (err) {
      console.error('Bulk assign failed:', err)
    }
  }

  return (
    <Box>
      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Box display="flex" alignItems="center" gap={1} mb={2} p={1} bgcolor="#e3f2fd" borderRadius={4} style={{ gap: 8 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {selected.size} selected
          </Typography>
          <Autocomplete
            options={services}
            getOptionLabel={o => o.name || o.serviceId || ''}
            value={bulkService}
            onChange={(_, v) => setBulkService(v)}
            renderInput={params => (
              <TextField {...params} placeholder="Pick service..." variant="outlined" size="small" style={{ minWidth: 280 }} />
            )}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleBulkAssign}
            disabled={!bulkService}
            style={{ textTransform: 'none' }}
          >
            Assign to All
          </Button>
        </Box>
      )}

      {/* Category-grouped accordions */}
      {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
        <Accordion key={category} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Chip
                label={category}
                size="small"
                style={{
                  backgroundColor: CAT_COLORS[category] || '#6b7280',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 11
                }}
              />
              <Typography variant="body2" color="textSecondary">
                {items.length} items &middot; {items.filter(i => i.serviceId).length} assigned
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ padding: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow style={{ backgroundColor: '#fafafa' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        onChange={() => {
                          const ids = items.map(i => i._id)
                          const allSelected = ids.every(id => selected.has(id))
                          const next = new Set(selected)
                          ids.forEach(id => allSelected ? next.delete(id) : next.add(id))
                          setSelected(next)
                        }}
                      />
                    </TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Description</TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 12 }} align="right">Qty</TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Service</TableCell>
                    <TableCell style={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(asm => {
                    const bi = asm.bidItem || {}
                    const sugg = suggestions[asm._id]
                    return (
                      <React.Fragment key={asm._id}>
                        <TableRow hover selected={selected.has(asm._id)}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={selected.has(asm._id)} onChange={() => toggleSelect(asm._id)} size="small" />
                          </TableCell>
                          <TableCell style={{ fontSize: 12 }}>{bi.itemNo || '-'}</TableCell>
                          <TableCell style={{ fontSize: 12, maxWidth: 350 }}>
                            {bi.descriptionRaw || bi.descriptionNormalized || '-'}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: 12 }}>{bi.quantity ?? '-'}</TableCell>
                          <TableCell>
                            {asm.serviceName ? (
                              <Chip
                                label={asm.serviceName}
                                size="small"
                                onDelete={() => handleAssignSingle(asm._id, null, null)}
                                style={{ fontSize: 11, fontWeight: 600 }}
                              />
                            ) : (
                              <Autocomplete
                                options={services}
                                getOptionLabel={o => o.name || o.serviceId || ''}
                                onChange={(_, v) => {
                                  if (v) handleAssignSingle(asm._id, v.serviceId, v.name)
                                }}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Pick service..." variant="outlined" size="small" />
                                )}
                                size="small"
                                style={{ minWidth: 220 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => handleSuggest(asm._id)}
                              disabled={!!loadingSuggestions[asm._id]}
                              style={{ textTransform: 'none', fontSize: 11 }}
                            >
                              {loadingSuggestions[asm._id] ? <CircularProgress size={14} /> : 'Suggest'}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {/* Suggestion row */}
                        {sugg && sugg.length > 0 && (
                          <TableRow>
                            <TableCell />
                            <TableCell colSpan={5}>
                              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                {sugg.map((s, i) => (
                                  <Chip
                                    key={i}
                                    label={`${s.serviceName} (${Math.round(s.score * 100)}%)`}
                                    size="small"
                                    clickable
                                    onClick={() => handleAssignSingle(asm._id, s.serviceId, s.serviceName)}
                                    style={{ fontSize: 11, backgroundColor: '#e8f5e9' }}
                                  />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      {Object.keys(groups).length === 0 && (
        <Box p={4} textAlign="center" color="#999">
          <Typography variant="body1">No assemblies with categories. Complete Stage 1 first.</Typography>
        </Box>
      )}
    </Box>
  )
}

export default ServiceStage
